import { useState, useEffect, useCallback } from "react";
import MUIDataTable from "mui-datatables";
import {
  Chip,
  TableCell,
  InputBase,
  Backdrop,
  CircularProgress,
  Tooltip,
  Grid,
  styled,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Box,
  Chip as MuiChip,
  Checkbox,
  Snackbar,
  Alert,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import { withStyles } from "@mui/styles";
import UpdateIcon from "@mui/icons-material/Update";
import ClearIcon from "@mui/icons-material/Clear";
import axios from "axios";

import "../../Styles/style.css";
import "./Ticket.css";
import AdminApiModule from "../../components/AdminApi/Api/api";
import TicketEditPincodePopup from "../../components/AdminApi/ticketEditPincodePopup";
import CloseTicketRemarksModal from "../../components/Components/CloseTicketRemark";
import AssignTechnicianPopup from "../../components/AdminApi/Api/assignTechnicianPopup";
import RemarksPopup from "../../components/AdminApi/Api/RemarksPopup";
import FileUploadErrors from "../../components/FileUploadErrors";
import RebookModal from "../../components/Components/RebookMoadl";
import { BASE_URL } from "../../components/config/config";
import PutOnHoldPopup from "./Pop-up/PutOnHoldPopup";

// Import permission utilities
import {
  canViewTickets,
  canViewAllTickets,
  canViewNormalTickets,
  canUpdateTicket,
  canDeleteTicket,
  canAssignUnassignTicket,
  canBroadcastTicket,
  canViewClosedTickets,
  canDownloadTicketList,
  canRemarkTicket,
  canViewContactNumber,
  canCloseTicket,
  hasFullAccess,
  canViewPincode,
  checkPermission,
} from "../../utility/permissionUtils";
import TicketFilterB2B from "./Filters/TicketFilterB2B";
import JobCompletedModal from "../../components/Components/CloseTicketRemark";

const CustomFilter = withStyles({
  filter: {
    background: "red",
  },
})(InputBase);

const Input = styled("input")({
  display: "none",
});

const stylesCss = {
  inputBox: {
    width: "100%",
    marginBottom: "10px",
  },
  gridStyle: {
    width: "80%",
    margin: "auto",
  },
  paddingInnerGrids: {
    paddingRight: "10px",
    paddingLeft: "10px",
    paddingTop: "10px",
  },
  actionButton: {
    minWidth: "140px",
    width: "140px",
    height: "40px",
    fontSize: "14px",
    margin: "2px",
    textTransform: "none",
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  smallActionButton: {
    minWidth: "40px",
    width: "140px",
    height: "40px",
    margin: "2px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
};

// Enhanced table options with better filtering
const getTableOptions = (filters, setFilters, hasDownloadTicketPermission) => ({
  filterType: "dropdown",
  filter: true,
  download: hasDownloadTicketPermission,
  downloadOptions: {
    filterOptions: {
      useDisplayedRowsOnly: true,
      useDisplayedColumnsOnly: true,
    },
  },

  // Custom filter display to show active filters
  customFilterDialogFooter: (currentFilterList, applyNewFilters) => {
    return (
      <div style={{ marginTop: "40px" }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {currentFilterList.map((filter, index) => {
            if (filter.length > 0) {
              return (
                <MuiChip
                  key={index}
                  label={`Filter ${index + 1}: ${filter.join(", ")}`}
                  onDelete={() => {
                    const newFilters = [...currentFilterList];
                    newFilters[index] = [];
                    applyNewFilters(newFilters);
                  }}
                  size="small"
                />
              );
            }
            return null;
          })}
        </Box>
      </div>
    );
  },

  // Enhanced filter menu with multi-select
  customFilterListOptions: {
    render: (value) => {
      if (value.length === 0) {
        return null;
      }
      return `${value.length} filter(s) active`;
    },
    update: (filterList, filterPos, index) => {
      const newFilterList = [...filterList];
      if (newFilterList[index].length === 0) {
        newFilterList[index] = [];
      }
      return newFilterList;
    },
  },

  // Persist filter state
  onFilterChange: (
    changedColumn,
    filterList,
    type,
    changedColumnIndex,
    displayData
  ) => {
    if (type === "reset") {
      setFilters([]);
    } else {
      setFilters(filterList);
    }
  },

  // Initialize with saved filters
  setFilterChipProps: () => {
    return {
      color: "primary",
      variant: "outlined",
    };
  },

  // Better filter display
  textLabels: {
    body: {
      noMatch: "Sorry, no matching records found",
      toolTip: "Sort",
      columnHeaderTooltip: (column) => `Filter ${column.label}`,
    },
    filter: {
      all: "All",
      title: "FILTERS",
      reset: "RESET",
    },
    viewColumns: {
      title: "Show Columns",
      titleAria: "Show/Hide Columns",
    },
    toolbar: {
      search: "Search",
      downloadCsv: "Download CSV",
      print: "Print",
      viewColumns: "Columns",
      filterTable: "Filters",
    },
    selectedRows: {
      text: "row(s) selected",
      delete: "Delete",
      deleteAria: "Delete Selected Rows",
    },
  },
});

// Status Radio Group Component
const StatusRadioGroup = ({ value, ticketId, assigned_by, onChange }) => {
  const getLabelStyle = (status) => ({
    color:
      value === status ? (status === "Assigned" ? "green" : "red") : "inherit",
    fontWeight: value === status ? "bold" : "normal",
  });

  return (
    <div style={{ textAlign: "center" }}>
      <RadioGroup
        row
        value={value}
        onChange={onChange}
        style={{ justifyContent: "center", marginBottom: "5px" }}
      >
        <FormControlLabel
          value="Not Assigned"
          control={<Radio color="primary" />}
          label={
            <span style={getLabelStyle("Not Assigned")}>
              Validation Pending
            </span>
          }
        />
        <FormControlLabel
          value="Assigned"
          control={<Radio color="primary" />}
          label={
            <span style={getLabelStyle("Assigned")}>Validation Completed</span>
          }
        />
      </RadioGroup>

      {assigned_by && assigned_by.trim() !== "" && (
        <Typography
          variant="caption"
          style={{
            display: "block",
            color: value === "Assigned" ? "green" : "red",
            fontWeight: "bold",
            fontSize: "12px",
            fontStyle: "italic",
          }}
        >
          By: {assigned_by}
        </Typography>
      )}
    </div>
  );
};

// Action Buttons Component
const ActionButtons = ({
  value,
  hasUpdateTicketPermission,
  hasCloseTicketPermission,
  hasBroadcastPermission,
  hasRemarkTicketPermission,
  hasPutOnHoldPermission,
  hasAssignUnassignPermission,
  handleViewTicket,
  handleEditTicket,
  handlePutOnHold,
  handleRemoveHold,
  isPuttingOnHold,
  isRemovingHold,
  clickCloseTicket,
  handleBroadcastButtonClick,
  handleRebookOpen,
  handleRemarks,
  reopen,
  handleOpenTechnicianPopup,
}) => {
  const isOnHold = value?.ticket_status === "ON_HOLD";
  const isProcessing = isPuttingOnHold || isRemovingHold;

  // Technician assignment logic
  const isTechnicianAssigned = value?.is_technician_assigned;
  const ticketStatus = value?.ticket_status;
  const isClosed = ticketStatus === "CLOSED";
  const isAccepted = ticketStatus === "ACCEPTED";
  const isPending = ticketStatus === "PENDING";

  // Extract service and pincode from ticket data
  const serviceId = value?.service_provided_for?._id;
  const serviceName = value?.service_provided_for?.service_name;
  const pincode = value?.address_details?.pincode;

  // Determine technician assignment eligibility
  const canAssignTechnician =
    !isClosed && !isTechnicianAssigned && (isPending || isAccepted);
  const canChangeTechnician = !isClosed && isTechnicianAssigned;

  // Check if required data is available for technician assignment
  const hasRequiredDataForAssignment = serviceId && pincode;

  // SIMPLIFIED close button conditions
  const canShowCloseButton =
    hasCloseTicketPermission &&
    value?.ticket_status !== "CLOSED" &&
    value?.ticket_status !== "ON_HOLD";
  // value?.is_technician_assigned;

  const isCloseButtonDisabled =
    !value?.service_available || !value?.assigned_ids?.assigned_center_id?._id;

  const handleHoldButtonClick = () => {
    if (isOnHold) {
      handleRemoveHold(value?._id);
    } else {
      handlePutOnHold(value);
    }
  };

  return (
    <div
      style={{
        gap: "4px",
        display: "flex",
        alignItems: "stretch",
      }}
    >
      <Button
        sx={stylesCss.actionButton}
        variant="contained"
        onClick={() => handleViewTicket(value?._id)}
      >
        View Request
      </Button>

      {hasUpdateTicketPermission && (
        <Button
          sx={stylesCss.actionButton}
          variant="contained"
          onClick={() => handleEditTicket(value?._id)}
        >
          Edit Request
        </Button>
      )}

      {/* Technician Assignment Buttons */}
      {!isClosed && hasAssignUnassignPermission && (
        <>
          {canAssignTechnician && (
            <Tooltip
              title={
                !hasRequiredDataForAssignment
                  ? `Missing: ${!serviceId ? "Service" : ""}${
                      !serviceId && !pincode ? " and " : ""
                    }${!pincode ? "Pincode" : ""}`
                  : `Assign technician for ${serviceName} in pincode ${pincode}`
              }
            >
              <span style={{ display: "flex" }}>
                {" "}
                <Button
                  sx={stylesCss.actionButton}
                  variant="contained"
                  color="primary"
                  onClick={() => handleOpenTechnicianPopup(value, false)}
                  disabled={!hasRequiredDataForAssignment}
                >
                  Assign Technician
                  {!hasRequiredDataForAssignment && " ⚠️"}
                </Button>
              </span>
            </Tooltip>
          )}

          {/* Change Technician Button - Show when technician is assigned */}
          {canChangeTechnician && (
            <Tooltip
              title={
                !value?.assigned_ids?.assigned_center_id?._id
                  ? "Center information missing"
                  : `Change technician from ${value?.assigned_ids?.assigned_center_id?.center_name}`
              }
            >
              <span style={{ display: "flex" }}>
                {" "}
                <Button
                  sx={stylesCss.actionButton}
                  variant="contained"
                  color="primary"
                  onClick={() => handleOpenTechnicianPopup(value, true)}
                  disabled={!value?.assigned_ids?.assigned_center_id?._id}
                >
                  Change Technician
                </Button>
              </span>
            </Tooltip>
          )}
        </>
      )}

      {/* Put on Hold / On Hold Button */}
      {hasPutOnHoldPermission && value?.ticket_status !== "CLOSED" && (
        <Button
          sx={{
            ...stylesCss.actionButton,
            backgroundColor: isOnHold ? "red" : "primary",
            "&:hover": {
              backgroundColor: isOnHold ? "#d32f2f" : "primary.dark",
            },
            "&:disabled": {
              backgroundColor: isOnHold ? "red" : "primary",
              opacity: 0.7,
            },
          }}
          variant="contained"
          onClick={handleHoldButtonClick}
          disabled={isProcessing}
        >
          {isProcessing
            ? "Processing..."
            : isOnHold
            ? "On Hold"
            : "Put on Hold"}
        </Button>
      )}

      {/* Close Request Button */}
      {canShowCloseButton && (
        <Button
          sx={{
            ...stylesCss.actionButton,
            backgroundColor: isCloseButtonDisabled ? "grey" : "primary",
            "&:hover": {
              backgroundColor: isCloseButtonDisabled ? "grey" : "primary.dark",
            },
          }}
          onClick={() => {
            clickCloseTicket(
              value?._id,
              value?.assigned_ids?.assigned_center_id?._id,
              value?.ticket_id
            );
          }}
          variant="contained"
          disabled={isCloseButtonDisabled}
        >
          {isCloseButtonDisabled ? "Cannot Close" : "Close Request"}
        </Button>
      )}

      {hasBroadcastPermission &&
        !value?.is_technician_assigned &&
        value?.ticket_status !== "ON_HOLD" && (
          <Button
            sx={stylesCss.actionButton}
            variant="contained"
            onClick={() => handleBroadcastButtonClick(value?._id)}
          >
            Broadcast
          </Button>
        )}

      {value?.ticket_status === "CLOSED" && hasUpdateTicketPermission && (
        <Tooltip title="Rebook">
          <span style={{ display: "flex" }}>
            {" "}
            <Button
              sx={stylesCss.smallActionButton}
              variant="contained"
              onClick={() => handleRebookOpen(value?._id)}
            >
              <UpdateIcon style={{ color: "green" }} />
              Rebook
            </Button>
          </span>
        </Tooltip>
      )}

      {hasRemarkTicketPermission && (
        <Button
          sx={{
            ...stylesCss.actionButton,
            backgroundColor: value?.remarks?.onboarding_team_remarks
              ? "green"
              : "#primary",
          }}
          onClick={() =>
            handleRemarks(
              value?._id,
              value?.remarks?.onboarding_team_remarks,
              value?.remarks?.onboarding_team_name
            )
          }
          variant="contained"
        >
          Remarks
        </Button>
      )}

      {value?.revisit_status && hasUpdateTicketPermission && (
        <Button
          sx={stylesCss.actionButton}
          style={{ backgroundColor: "purple" }}
          onClick={() => reopen(value?._id)}
        >
          Reopen-Log
        </Button>
      )}
    </div>
  );
};

const AllClientTickets = () => {
  const [ticketsList, setTicketsList] = useState([]);
  const [isEditOpenAddressPincode, setIsEditOpenAddressPincode] =
    useState(false);
  const [isEditOpenTicketDetails, setIsEditOpenTicketDetails] = useState({});
  const [isPincodeUpdating, setIsPincodeUpdating] = useState(false);
  const [getSingleTicketData, setGetSingleTicketData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTicketIds, setSelectedTicketIds] = useState([]);
  const [doesFileUploadErrorExists, setDoesFileUploadErrorExists] =
    useState(false);
  const [errorsList, setErrorsList] = useState({});
  const [selectedRowsClosingPrice, setSelectedRowsClosingPrice] = useState({});
  const [totalClosingPrice, setTotalClosingPrice] = useState(0);
  const [isAssignTechnicianPopupOpen, setIsAssignTechnicianPopupOpen] =
    useState(false);
  const [isRemarksPopupOpen, setIsRemarksPopupOpen] = useState(false);
  const [isDeleteAlertPopupOpen, setIsDeleteAlertPopupOpen] = useState(false);
  const [deleteHeading, setDeleteHeading] = useState("Ticket");
  const [
    ticketDetailsForAssignTechnician,
    setTicketDetailsForAssignTechnician,
  ] = useState({});
  const [isTechnicianAssigning, setIsTechnicianAssigning] = useState(false);
  const [technicianList, setTechnicianList] = useState([]);
  const [isCloseTicketRemarksModalOpen, setIsCloseTicketRemarksModalOpen] =
    useState(false);
  const [closingTicketId, setClosingTicketId] = useState("");
  const [deleteTicketId, setDeleteTicketId] = useState("");
  const [rebookTicketId, setRebookTicketId] = useState("");
  const [centerAssignId, setCenterAssignId] = useState("");
  const [isForChange, setIsForChange] = useState(false);
  const [openRebook, setOpenRebook] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [username, setUsername] = useState("");
  const [initialRemarks, setInitialRemarks] = useState("");
  const [processedname, setProcessedname] = useState("");
  const [isPutOnHoldPopupOpen, setIsPutOnHoldPopupOpen] = useState(false);
  const [selectedTicketForHold, setSelectedTicketForHold] = useState(null);
  const [isPuttingOnHold, setIsPuttingOnHold] = useState(false);
  const [isRemovingHold, setIsRemovingHold] = useState(false);

  // Add new state for snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // New state for persistent filters - Load from localStorage on initial render
  const [appliedFilters, setAppliedFilters] = useState(() => {
    const savedFilters = localStorage.getItem("ticketFiltersB2B");
    return savedFilters ? JSON.parse(savedFilters) : {};
  });
  const [isInitialized, setIsInitialized] = useState(false);

  const [isFilterLoading, setIsFilterLoading] = useState(false);

  const navigate = useNavigate();
  const reduxState = useSelector((state) => state.userReducer);
  const globalState = useSelector((state) => state.AdminReducer);
  const accessToken = globalState?.userTokens?.accessToken;

  // Get user permissions from redux state
  const userPermissions = reduxState?.adminDetails?.role_id?.permissions;
  const isSuperAdmin =
    reduxState?.adminDetails?.role_id?.role === "SUPER_ADMIN";

  // Permission checks
  const hasViewTicketPermission = canViewTickets(userPermissions);
  const hasViewAllTicketsPermission = canViewAllTickets(userPermissions);
  const hasViewNormalTicketsPermission = canViewNormalTickets(userPermissions);
  const hasUpdateTicketPermission =
    canUpdateTicket(userPermissions) || hasFullAccess(userPermissions);
  const hasDeleteTicketPermission =
    canDeleteTicket(userPermissions) || hasFullAccess(userPermissions);
  const hasAssignUnassignPermission =
    canAssignUnassignTicket(userPermissions) || hasFullAccess(userPermissions);
  const hasBroadcastPermission =
    canBroadcastTicket(userPermissions) || hasFullAccess(userPermissions);
  const hasCloseTicketPermission =
    canCloseTicket(userPermissions) || hasFullAccess(userPermissions);
  const hasRemarkTicketPermission =
    canRemarkTicket(userPermissions) || hasFullAccess(userPermissions);
  const hasViewContactNumberPermission =
    canViewContactNumber(userPermissions) || hasFullAccess(userPermissions);
  const hasViewPincodePermission = canViewPincode(userPermissions);
  const hasPutOnHoldPermission =
    checkPermission(
      userPermissions,
      "ticket_details.update_ticket_details.put_on_hold_ticket"
    ) || hasFullAccess(userPermissions);
  const hasDownloadTicketPermission =
    canDownloadTicketList(userPermissions) || hasFullAccess(userPermissions);

  // Snackbar handlers
  const handleSnackbarOpen = (message, severity = "success") => {
    setSnackbarOpen(true);
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Function to fetch technicians based on ticket status, service, and pincode
  const fetchTechniciansForTicket = async (ticketDetails) => {
    try {
      let technicians = [];

      if (ticketDetails.is_technician_assigned) {
        const centerId = ticketDetails.assigned_ids?.assigned_center_id?._id;

        if (centerId) {
          const response = await axios.get(
            `${BASE_URL}admin/api/center/${centerId}/technicians`
          );

          if (response.data.success) {
            technicians = response.data.data.map((technician) => ({
              _id: technician._id,
              name: technician.personal_details.name,
              pending_ticket_count:
                technician.count_details.pending_ticket_count,
              phone: technician.personal_details.primary_phone?.mobile_number,
              expertise: technician.service_details?.services_provided || [],
            }));
          }
        }
      } else {
        const serviceId = ticketDetails.service_provided_for?._id;
        const pincode = ticketDetails.address_details?.pincode;

        if (!serviceId || !pincode) {
          throw new Error(
            `Service ID or Pincode not available for this ticket. Service: ${serviceId}, Pincode: ${pincode}`
          );
        }

        const params = {
          primary_services: serviceId,
          pincode: pincode,
        };

        const response = await axios.get(
          `${BASE_URL}admin/get-all-technician`,
          { params }
        );

        if (response.data.success) {
          technicians = response.data.data.map((technician) => ({
            _id: technician._id,
            name: technician.personal_details.name,
            pending_ticket_count: technician.count_details.pending_ticket_count,
            phone: technician.personal_details.phone?.mobile_number,
            expertise: technician.service_details?.services_provided || [],
            city: technician.service_area?.city,
            pincodes: technician.service_area?.pincodes || [],
            is_active: technician.is_active,
            is_otp_verified: technician.is_otp_verified,
          }));

          if (technicians.length === 0) {
            console.log("No technicians found with exact pincode.");
          }
        }
      }

      return technicians;
    } catch (error) {
      console.error("Error fetching technicians:", error);
      throw error;
    }
  };

  // Technician Popup Handlers
  const handleOpenTechnicianPopup = async (ticketDetails, isForChangeValue) => {
    if (!hasAssignUnassignPermission) {
      handleSnackbarOpen(
        "You don't have permission to assign technicians",
        "error"
      );
      return;
    }

    try {
      setIsLoading(true);
      const technicians = await fetchTechniciansForTicket(ticketDetails);

      setIsAssignTechnicianPopupOpen(true);
      setTicketDetailsForAssignTechnician(ticketDetails);
      setIsForChange(isForChangeValue);
      setTechnicianList(technicians);
    } catch (error) {
      console.error("Error opening technician popup:", error);
      handleSnackbarOpen("Failed to fetch technicians", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssignTechnician = async (technicianId) => {
    if (!technicianId || !ticketDetailsForAssignTechnician?._id) {
      handleSnackbarOpen("Please select a technician", "error");
      return;
    }

    try {
      setIsTechnicianAssigning(true);

      const payload = {
        ticket_obj_id: ticketDetailsForAssignTechnician._id,
        technician_obj_id: technicianId,
      };

      const response = await axios.post(
        `${BASE_URL}admin/assigning-ticket-technician`,
        payload
      );

      if (response.data.success) {
        handleSnackbarOpen("Technician assigned successfully", "success");
        handleCloseAssignTechnicianPopup();
        fetchData(appliedFilters);
      } else {
        throw new Error(response.data.message || "Failed to assign technician");
      }
    } catch (error) {
      console.error("Error assigning technician:", error);
      handleSnackbarOpen(
        error.response?.data?.message || "Failed to assign technician",
        "error"
      );
    } finally {
      setIsTechnicianAssigning(false);
    }
  };

  const handleChangeTechnician = async (technicianId) => {
    if (!technicianId || !ticketDetailsForAssignTechnician?._id) {
      handleSnackbarOpen("Please select a technician", "error");
      return;
    }

    try {
      setIsTechnicianAssigning(true);

      const centerId =
        ticketDetailsForAssignTechnician.assigned_ids?.assigned_center_id?._id;
      if (!centerId) {
        throw new Error("Center ID not found");
      }

      const payload = {
        ticket_obj_id: ticketDetailsForAssignTechnician._id,
        technician_obj_id: technicianId,
        center_id: centerId,
      };

      const response = await axios.post(
        `${BASE_URL}admin/assigning-ticket-technician`,
        payload
      );

      if (response.data.success) {
        handleSnackbarOpen("Technician changed successfully", "success");
        handleCloseAssignTechnicianPopup();
        fetchData(appliedFilters);
      } else {
        throw new Error(response.data.message || "Failed to change technician");
      }
    } catch (error) {
      console.error("Error changing technician:", error);
      handleSnackbarOpen(
        error.response?.data?.message || "Failed to change technician",
        "error"
      );
    } finally {
      setIsTechnicianAssigning(false);
    }
  };

  const handleCloseAssignTechnicianPopup = () => {
    setIsAssignTechnicianPopupOpen(false);
    setTicketDetailsForAssignTechnician({});
    setTechnicianList([]);
    setIsTechnicianAssigning(false);
  };

  // Helper Functions
  const getFormattedAddress = (addressObject) => {
    if (!addressObject) return "";
    return `${addressObject.house_number || ""}, ${
      addressObject.locality || ""
    }, ${addressObject.city || ""}, ${addressObject.state || ""}`;
  };

  const getParsedData = (data) => {
    return data.map((item) => {
      const closingPrice =
        item?.closing_ticket_price === "-" || !item?.closing_ticket_price
          ? 0
          : parseFloat(item.closing_ticket_price);

      const isAssigned = item?.mark_ticket?.mark_ticket_assigned || false;
      const assignedBy = item?.mark_ticket?.mark_ticket_assigned_by || "";

      return {
        Created_at: new Date(item?.createdAt).toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
          month: "long",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
          second: "numeric",
          hour12: true,
        }),
        daysDifference: moment().diff(moment(item?.createdAt), "days"),
        Image: item?.onsite_pictures,
        ClientQRImages: item?.client_onsite_pictures || [], // Add Client QR Images field
        assigned_status: isAssigned ? "Assigned" : "Not Assigned",
        assigned_by: assignedBy,
        is_assigned: isAssigned,
        ticket_id: item?.ticket_id || "-",
        Qr_Id: item?.assigned_ids?.assigned_center_id?.qr_details?.qr_id || "-",
        center_id: item?.assigned_ids?.assigned_center_id?.center_name,
        name: item.personal_details.name,
        phone:
          isSuperAdmin || hasViewContactNumberPermission
            ? item.personal_details.primary_phone.mobile_number
            : "*****",
        pincode: hasViewPincodePermission
          ? item.address_details.pincode
          : "*****",
        city: item.address_details.city || "-",
        client_id: item?.service_provided_client_id || "-",
        client_name: item?.client_name || "-",
        specific_requirements: item?.specific_requirement || "-",
        bstatus: getBroadcastStatus(item),
        tstatus: getTicketStatus(item),
        tcreatedby:
          item?.ticket_created_by === "SUPER_ADMIN"
            ? item?.create_name
            : item?.ticket_created_by,
        action: item,
        closingPrice: closingPrice,
        timeprefrence: getTimePreference(item),
        paidbytechnician: item?.is_paid_by_technician ? "YES" : "NO",
        Center_QR: item?.created_by?.qr_details?.qr_id,
        ticket_obj_id: item?._id,
        ticket_status: item?.ticket_status,
        serial_number: item?.serial_number || "-",
      };
    });
  };

  const getBroadcastStatus = (item) => {
    if (!item?.service_available) return "No Service Available In This Area";
    if (
      item?.broadcast_status === "broadcasted" &&
      item?.ticket_created_by === "CENTER"
    )
      return "NEW NOT PAID";
    if (
      item?.broadcast_status === "broadcasted" &&
      item?.ticket_created_by === "PUBLIC_WITH_DELAR_QR"
    )
      return "DEALER";
    if (
      item?.ticket_created_by === "SUPER_ADMIN" &&
      item?.broadcast_status === "accepted after broadcast"
    )
      return "Admin Created-Accepted";
    if (item?.broadcast_status === "accepted after broadcast")
      return "Public Created-Accepted";
    if (!item?.broadcast_status) return "Not Paid (Payment Failed)";
    if (item?.broadcast_status === "broadcasted") {
      return `NEW ${(item?.is_paid_by_public && "99") || "NOT PAID"}`;
    }
    return `${item.broadcast_status} FREE` || "-";
  };

  const getTicketStatus = (item) => {
    if (
      item?.ticket_status === "PENDING" &&
      item?.broadcast_status === "matched_in_same_center"
    ) {
      return item?.assigned_ids?.assigned_technician_id?.personal_details?.name
        ? "Technician Assigned"
        : "Technician Not Assigned";
    }
    if (item?.ticket_status === "ACCEPTED") {
      return item?.assigned_ids?.assigned_technician_id?.personal_details?.name
        ? "Technician Assigned"
        : "Technician Not Assigned";
    }
    return item?.ticket_status;
  };

  const getTimePreference = (item) => {
    if (item?.time_preference?.specific_date_time) {
      return new Date(item.time_preference.specific_date_time).toLocaleString(
        "en-IN",
        {
          timeZone: "Asia/Kolkata",
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
          second: "numeric",
          hour12: true,
        }
      );
    }
    return item?.time_preference?.time_preference_type;
  };

  // API Calls - Updated to use appliedFilters
  const fetchData = useCallback(
    async (filtersToApply = appliedFilters) => {
      if (!hasViewTicketPermission) return;

      setIsLoading(true);
      setIsFilterLoading(true);

      try {
        const queryParams = new URLSearchParams();
        Object.entries(filtersToApply).forEach(([key, value]) => {
          if (value !== "" && value !== null && value !== undefined) {
            if (typeof value === "boolean") {
              queryParams.append(key, value.toString());
            } else if (key === "start_date" || key === "end_date") {
              queryParams.append(key, moment(value).format("YYYY-MM-DD"));
            } else {
              queryParams.append(key, value);
            }
          }
        });

        const url = `${BASE_URL}admin/get-all-B2B-ticket?${queryParams.toString()}`;
        const res = await fetch(url, {
          headers: {
            "x-access-token": accessToken,
            "Content-Type": "application/json",
          },
        });

        if (!res.ok) throw new Error("Network response was not ok");
        const data = await res.json();

        if (data?.data?.length) {
          setTicketsList(getParsedData(data.data));
        } else {
          setTicketsList([]);
        }
      } catch (err) {
        console.error("Error fetching tickets:", err);
        handleSnackbarOpen("Failed to fetch tickets", "error");
      } finally {
        setIsLoading(false);
        setIsFilterLoading(false);
      }
    },
    [hasViewTicketPermission, accessToken]
  );

  // Updated filter handler
  const handleFilterSubmit = async (newFilters) => {
    setAppliedFilters(newFilters);
    // Save to localStorage
    localStorage.setItem("ticketFiltersB2B", JSON.stringify(newFilters));
    await fetchData(newFilters);
  };
  // Clear all filters
  const handleClearAllFilters = () => {
    const emptyFilters = {};
    setAppliedFilters(emptyFilters);
    localStorage.removeItem("ticketFiltersB2B");
    fetchData(emptyFilters);
  };

  const fetchTechnicians = useCallback(async () => {
    try {
      const res = await new AdminApiModule().getAllTechnician();
      setTechnicianList(res?.data || []);
    } catch (err) {
      console.error("Error fetching technicians:", err);
    }
  }, []);

  // Event Handlers
  const handleAssignedStatusChange = async (ticketId, newStatus) => {
    if (!hasAssignUnassignPermission) {
      handleSnackbarOpen(
        "You don't have permission to assign/unassign tickets",
        "error"
      );
      return;
    }

    const payload = {
      ticket_obj_id: ticketId,
      isAssigned: newStatus === "Assigned",
    };

    try {
      const response = await fetch(
        `${BASE_URL}admin/mark-ticket-assigned-notassigned`,
        {
          method: "POST",
          headers: {
            "x-access-token": accessToken,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error("Network response was not ok");

      const res = await response.json();
      if (res.success) {
        handleSnackbarOpen("Status updated successfully", "success");
        fetchData(appliedFilters);
      }
    } catch (error) {
      console.error("Error updating assigned status:", error);
      handleSnackbarOpen("Failed to update status", "error");
    }
  };

  const handleBroadcastButtonClick = async (id) => {
    if (!hasBroadcastPermission) {
      handleSnackbarOpen(
        "You don't have permission to broadcast tickets",
        "error"
      );
      return;
    }

    try {
      await fetch(`${BASE_URL}technicianApp/ticket-broadcast`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ticket_obj_id: id }),
      });
      handleSnackbarOpen("Ticket broadcasted successfully", "success");
      fetchData(appliedFilters);
    } catch (error) {
      console.error("Error broadcasting ticket:", error);
      handleSnackbarOpen("Failed to broadcast ticket", "error");
    }
  };

  // Enhanced Close Ticket Handler
  const handleCloseTicket = async (
    closingTicketRemarks,
    closingPrice,
    closingCode,
    reasonToClose
  ) => {
    if (!hasCloseTicketPermission) {
      handleSnackbarOpen("You don't have permission to close tickets", "error");
      return;
    }

    if (!closingPrice && closingPrice !== 0) return;

    if (closingTicketId) {
      const payload = {
        center_id: centerAssignId,
        ticket_obj_id: closingTicketId,
        remarks: {
          close_ticket_remarks: reasonToClose,
          technician_remarks: closingTicketRemarks,
        },
        amount: parseInt(closingPrice),
        code: closingCode,
      };

      try {
        await new AdminApiModule().closeTicket(payload);
        setClosingTicketId("");
        handleCloseCloseTicketRemarksModal();
        handleSnackbarOpen("Ticket closed successfully", "success");
        fetchData(appliedFilters);
      } catch (err) {
        console.error("Error closing ticket:", err);
        handleSnackbarOpen("Failed to close ticket", "error");
      }
    }
  };

  // Enhanced clickCloseTicket Function
  const clickCloseTicket = (ticket_obj_id, centerAssignId, ticketId) => {
    if (!hasCloseTicketPermission) {
      handleSnackbarOpen("You don't have permission to close tickets", "error");
      return;
    }

    // Validate required data
    if (!ticket_obj_id) {
      console.error("Missing ticket_obj_id");
      handleSnackbarOpen("Missing ticket information", "error");
      return;
    }

    if (!centerAssignId) {
      console.error("Missing centerAssignId");
      handleSnackbarOpen(
        "Cannot close ticket: No center assignment found",
        "error"
      );
      return;
    }

    // Set the ticket ID and open the modal
    setClosingTicketId(ticket_obj_id);
    setCenterAssignId(centerAssignId);

    // IMPORTANT: Open the modal
    handleOpenCloseTicketRemarksModal();

    localStorage.setItem("tickeClosetId", ticketId);
    localStorage.setItem("tickeClose_Id", ticket_obj_id);
  };

  const handleUpdatePincode = async (newPincode) => {
    if (!hasUpdateTicketPermission) {
      handleSnackbarOpen(
        "You don't have permission to update ticket pincode",
        "error"
      );
      return;
    }

    if (!newPincode) return;

    const payload = {
      address_details: { pincode: newPincode },
      ticket_obj_id: isEditOpenTicketDetails?._id,
    };

    setIsPincodeUpdating(true);
    try {
      await new AdminApiModule().updatePincode(payload);
      fetchData(appliedFilters);
      handleCloseEditPincodePopup();
      handleSnackbarOpen("Pincode updated successfully", "success");
    } catch (err) {
      console.error("Error updating pincode:", err);
      handleSnackbarOpen("Failed to update pincode", "error");
    } finally {
      setIsPincodeUpdating(false);
    }
  };

  // PUT ON HOLD HANDLERS AND FUNCTIONALITY
  const handlePutOnHold = async (payload) => {
    if (!hasPutOnHoldPermission) {
      handleSnackbarOpen(
        "You don't have permission to put tickets on hold",
        "error"
      );
      return;
    }

    setIsPuttingOnHold(true);
    try {
      const response = await fetch(
        `${BASE_URL}admin/put-ticket-on-hold-by-admin`,
        {
          method: "POST",
          headers: {
            "x-access-token": accessToken,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error("Network response was not ok");

      const res = await response.json();
      if (res.success) {
        fetchData(appliedFilters);
        handleClosePutOnHoldPopup();
        handleSnackbarOpen("Ticket put on hold successfully", "success");
      } else {
        throw new Error(res.message || "Failed to put ticket on hold");
      }
    } catch (error) {
      console.error("Error putting ticket on hold:", error);
      handleSnackbarOpen("Failed to put ticket on hold", "error");
    } finally {
      setIsPuttingOnHold(false);
    }
  };

  const handleRemoveHold = async (ticketId) => {
    if (!hasPutOnHoldPermission) {
      handleSnackbarOpen(
        "You don't have permission to remove ticket hold",
        "error"
      );
      return;
    }

    setIsRemovingHold(true);
    try {
      const response = await fetch(
        `${BASE_URL}admin/remove-put-ticket-on-hold-by-admin`,
        {
          method: "POST",
          headers: {
            "x-access-token": accessToken,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ticket_obj_id: ticketId,
          }),
        }
      );

      if (!response.ok) throw new Error("Network response was not ok");

      const res = await response.json();
      if (res.success) {
        fetchData(appliedFilters);
        handleSnackbarOpen("Ticket hold removed successfully", "success");
      } else {
        throw new Error(res.message || "Failed to remove ticket hold");
      }
    } catch (error) {
      console.error("Error removing ticket hold:", error);
      handleSnackbarOpen("Failed to remove ticket hold", "error");
    } finally {
      setIsRemovingHold(false);
    }
  };

  // Navigation Handlers
  const reopen = (id) => {
    if (!hasUpdateTicketPermission) {
      handleSnackbarOpen(
        "You don't have permission to reopen tickets",
        "error"
      );
      return;
    }
    navigate(`/reopen-log/${id}`);
  };

  const handleEditTicket = (id) => {
    if (!hasUpdateTicketPermission) {
      handleSnackbarOpen("You don't have permission to edit tickets", "error");
      return;
    }
    navigate("/admin-ticket?edit=" + id);
  };

  const handleViewTicket = (id) =>
    window.open(`/#/view-ticket?ticketId=${id}`, "_blank");

  const handleViewwTicket = (images, id, ticketData) => {
    // Pass ticket_verification info and full ticket data if available
    const ticketVerification = ticketData?.ticket_verification || null;
    const isVerified = ticketVerification?.verification_status === "VERIFIED";

    // Store state in sessionStorage for new tab to access
    const stateData = {
      imageUrls: images,
      ticketId: id,
      ticketVerification: ticketVerification,
      ticketData: ticketData, // Pass full ticket data for serial_number access
      isVerified: isVerified,
    };

    sessionStorage.setItem("ticketDocumentState", JSON.stringify(stateData));

    // Open in new tab (no window features = opens as new tab)
    window.open(`/#/ticketDocument`, "_blank");
  };

  // Other Handlers
  const handleRemarks = (ticketId, initialRemarks, processedname) => {
    if (!hasRemarkTicketPermission) {
      handleSnackbarOpen("You don't have permission to add remarks", "error");
      return;
    }
    setIsRemarksPopupOpen(true);
    setSelectedTicketId(ticketId);
    setInitialRemarks(initialRemarks);
    setProcessedname(processedname);
  };

  const handleRebookOpen = (rebookTicketId) => {
    if (!hasUpdateTicketPermission) {
      handleSnackbarOpen(
        "You don't have permission to rebook tickets",
        "error"
      );
      return;
    }
    setRebookTicketId(rebookTicketId);
    setOpenRebook(true);
  };

  // Enhanced Modal Handlers
  const handleOpenCloseTicketRemarksModal = () => {
    setIsCloseTicketRemarksModalOpen(true);
  };

  const handleCloseCloseTicketRemarksModal = () => {
    setIsCloseTicketRemarksModalOpen(false);
    setClosingTicketId("");
    setCenterAssignId("");
  };

  // Popup Handlers
  const handleCloseRemarksPopup = () => {
    setIsRemarksPopupOpen(false);
    setSelectedTicketId(null);
  };

  const handleCloseEditPincodePopup = () => {
    setIsEditOpenAddressPincode(false);
    setIsEditOpenTicketDetails({});
  };

  const handleRebookClose = () => setOpenRebook(false);

  // Popup handlers
  const handleOpenPutOnHoldPopup = (ticketData) => {
    setSelectedTicketForHold(ticketData);
    setIsPutOnHoldPopupOpen(true);
  };

  const handleClosePutOnHoldPopup = () => {
    setIsPutOnHoldPopupOpen(false);
    setSelectedTicketForHold(null);
  };

  // Columns Configuration with permission checks
  const columns = [
    {
      name: "Created_at",
      label: "Created on",
      options: {
        customHeadRender: () => (
          <TableCell
            style={{ fontSize: "17px", fontWeight: "600" }}
            align="center"
          >
            Created On
          </TableCell>
        ),
        filter: false,
        sort: true,
      },
    },
    {
      name: "daysDifference",
      label: "Aging",
      options: {
        customHeadRender: () => (
          <TableCell
            style={{ fontSize: "17px", fontWeight: "600" }}
            align="center"
          >
            Aging
          </TableCell>
        ),
        filter: false,
        sort: true,
      },
    },
    {
      name: "Image",
      label: "On-Site Image",
      options: {
        filter: false,
        sort: false,
        customHeadRender: () => (
          <TableCell
            style={{ fontSize: "17px", fontWeight: "600" }}
            align="center"
          >
            On-Site Image
          </TableCell>
        ),
        customBodyRender: (value, tableMeta) => {
          const rowData = tableMeta.rowData;
          const ticketData = rowData[rowData.length - 1];
          const ticketObjId = ticketData?._id;
          const isVerified =
            ticketData?.ticket_verification?.verification_status === "VERIFIED";

          return value?.length > 0 ? (
            <Button
              className="text-transform-none"
              variant="contained"
              sx={{
                backgroundColor: isVerified ? "#4caf50" : "primary.main",
                "&:hover": {
                  backgroundColor: isVerified ? "#45a049" : "primary.dark",
                },
              }}
              onClick={() => handleViewwTicket(value, ticketObjId, ticketData)}
            >
              Views
            </Button>
          ) : null;
        },
      },
    },
    {
      name: "ClientQRImages",
      label: "Client QR Images",
      options: {
        filter: false,
        sort: false,
        customHeadRender: () => (
          <TableCell
            style={{ fontSize: "17px", fontWeight: "600" }}
            align="center"
          >
            Client QR Images
          </TableCell>
        ),
        customBodyRender: (value, tableMeta) => {
          const rowData = tableMeta.rowData;
          const ticketData = rowData[rowData.length - 1];
          const ticketObjId = ticketData?._id;
          const isVerified =
            ticketData?.ticket_verification?.verification_status === "VERIFIED";

          return value?.length > 0 ? (
            <Button
              className="text-transform-none"
              variant="contained"
              sx={{
                backgroundColor: isVerified ? "#4caf50" : "primary.main",
                "&:hover": {
                  backgroundColor: isVerified ? "#45a049" : "primary.dark",
                },
              }}
              onClick={() => handleViewwTicket(value, ticketObjId, ticketData)}
            >
              Views
            </Button>
          ) : null;
        },
      },
    },
    {
      name: "assigned_by",
      label: "Assigned By",
      options: {
        filter: false,
        display: "excluded",
        sort: true,
      },
    },
    {
      name: "assigned_status",
      label: "Assigned Status",
      options: {
        filter: true,
        sort: true,
        filterType: "multiselect",
        filterOptions: {
          logic: (value, filters) => {
            if (filters.length === 0) return true;
            return filters.includes(value);
          },
        },
        customHeadRender: () => (
          <TableCell
            style={{ fontSize: "17px", fontWeight: "600" }}
            align="center"
          >
            Assigned Status
          </TableCell>
        ),
        customBodyRender: (value, tableMeta, updateValue) => {
          const rowData = tableMeta.rowData;
          const assigned_by = rowData[4] || "";
          const ticketData = rowData[rowData.length - 1];
          const ticketId = ticketData?._id;

          const handleChange = (event) => {
            const newValue = event.target.value;
            handleAssignedStatusChange(ticketId, newValue);
            updateValue(newValue);
          };

          return (
            <StatusRadioGroup
              value={value}
              ticketId={ticketId}
              assigned_by={assigned_by}
              onChange={handleChange}
            />
          );
        },
      },
    },
    {
      name: "ticket_id",
      label: "Ticket Id",
      options: {
        filter: true,
        sort: true,
        filterType: "textField",
        customHeadRender: () => (
          <TableCell
            style={{ fontSize: "17px", fontWeight: "600" }}
            align="center"
          >
            Request Id
          </TableCell>
        ),
      },
    },
    {
      name: "client_id",
      label: "Client Request Id",
      options: {
        filter: true,
        sort: true,
        filterType: "textField",
        customHeadRender: () => (
          <TableCell
            style={{ fontSize: "17px", fontWeight: "600" }}
            align="center"
          >
            Client Request Id
          </TableCell>
        ),
      },
    },
    {
      name: "serial_number",
      label: "Serial Number",
      options: {
        filter: true,
        sort: false,
        filterType: "textField",
        customHeadRender: () => (
          <TableCell
            style={{ fontSize: "17px", fontWeight: "600" }}
            align="center"
          >
            Serial Number
          </TableCell>
        ),
      },
    },
    {
      name: "tstatus",
      label: "Request status",
      options: {
        filter: true,
        sort: false,
        filterType: "multiselect",
        filterOptions: {
          names: [
            "Technician Assigned",
            "PENDING",
            "Technician Not Assigned",
            "CLOSED",
            "ON_HOLD",
          ],
        },
        customHeadRender: () => (
          <TableCell
            style={{ fontSize: "17px", fontWeight: "600" }}
            align="center"
          >
            Request status
          </TableCell>
        ),
        customBodyRender: (value) => {
          const statusColors = {
            "Technician Assigned": "#8FD1AD",
            PENDING: "#FF9933",
            "Technician Not Assigned": "#E76161",
            VERIFIED: "#00CC00",
            CLOSED: "#FF9999",
            ON_HOLD: "#FF6B6B",
          };

          return (
            <Chip
              label={value}
              style={{ background: statusColors[value] || "red" }}
            />
          );
        },
      },
    },
    {
      name: "bstatus",
      label: "Broadcast status",
      options: {
        filter: false,
        sort: false,
        filterType: "multiselect",
        customHeadRender: () => (
          <TableCell
            style={{ fontSize: "17px", fontWeight: "600" }}
            align="center"
          >
            Broadcast status
          </TableCell>
        ),
      },
    },
    {
      name: "paidbytechnician",
      label: "Paid By technician",
      options: {
        filter: true,
        sort: false,
        filterType: "multiselect",
        filterOptions: {
          names: ["YES", "NO"],
        },
        customHeadRender: () => (
          <TableCell
            style={{ fontSize: "17px", fontWeight: "600" }}
            align="center"
          >
            Paid By Technician
          </TableCell>
        ),
      },
    },
    {
      name: "center_id",
      label: "Assigned Center",
      options: {
        filter: false,
        sort: true,
        filterType: "multiselect",
        customHeadRender: () => (
          <TableCell
            style={{ fontSize: "17px", fontWeight: "600" }}
            align="center"
          >
            Assigned Center
          </TableCell>
        ),
      },
    },
    {
      name: "Qr_Id",
      label: "Qr_Id",
      options: {
        filter: true,
        sort: false,
        filterType: "textField",
        customHeadRender: () => (
          <TableCell
            style={{ fontSize: "17px", fontWeight: "600" }}
            align="center"
          >
            Qr_Id
          </TableCell>
        ),
      },
    },
    {
      name: "specific_requirements",
      label: "Specific Requirements",
      options: {
        filter: false,
        sort: false,
        filterType: "multiselect",
        customHeadRender: () => (
          <TableCell
            style={{ fontSize: "17px", fontWeight: "600" }}
            align="center"
          >
            Specific Requirements
          </TableCell>
        ),
      },
    },
    {
      name: "name",
      label: "Name",
      options: {
        filter: false,
        sort: false,
        filterType: "textField",
        customHeadRender: () => (
          <TableCell
            style={{ fontSize: "17px", fontWeight: "600" }}
            align="center"
          >
            Name
          </TableCell>
        ),
      },
    },
    {
      name: "phone",
      label: "Phone Number",
      options: {
        filter: false,
        sort: false,
        display: isSuperAdmin || hasViewContactNumberPermission,
        customHeadRender: () => (
          <TableCell
            style={{ fontSize: "17px", fontWeight: "600" }}
            align="center"
          >
            Phone Number
          </TableCell>
        ),
      },
    },
    {
      name: "pincode",
      label: "Pincode",
      options: {
        filter: true,
        sort: false,
        filterType: "multiselect",
        display: hasViewPincodePermission,
        customHeadRender: () => (
          <TableCell
            style={{ fontSize: "17px", fontWeight: "600" }}
            align="center"
          >
            Pincode
          </TableCell>
        ),
      },
    },
    {
      name: "city",
      label: "City",
      options: {
        filter: true,
        sort: false,
        filterType: "multiselect",
        customHeadRender: () => (
          <TableCell
            style={{ fontSize: "17px", fontWeight: "600" }}
            align="center"
          >
            City
          </TableCell>
        ),
      },
    },
    {
      name: "closingPrice",
      label: "Closing Price",
      options: {
        filter: false,
        sort: false,
        customHeadRender: () => (
          <TableCell
            style={{ fontSize: "17px", fontWeight: "600" }}
            align="center"
          >
            Closing Price
          </TableCell>
        ),
      },
    },
    {
      name: "client_name",
      label: "Client Name",
      options: {
        filter: true,
        sort: false,
        filterType: "multiselect",
        customHeadRender: () => (
          <TableCell
            style={{ fontSize: "17px", fontWeight: "600" }}
            align="center"
          >
            Client Name
          </TableCell>
        ),
      },
    },
    {
      name: "tcreatedby",
      label: "Ticket Created By",
      options: {
        filter: true,
        sort: true,
        filterType: "multiselect",
        customHeadRender: () => (
          <TableCell
            style={{ fontSize: "17px", fontWeight: "600" }}
            align="center"
          >
            Request Created By
          </TableCell>
        ),
      },
    },
    {
      name: "Center_QR",
      label: "Center QR Id",
      options: {
        filter: true,
        sort: false,
        filterType: "textField",
        customHeadRender: () => (
          <TableCell
            style={{ fontSize: "17px", fontWeight: "600" }}
            align="center"
          >
            Center_QR
          </TableCell>
        ),
      },
    },
    {
      name: "timeprefrence",
      label: "Time Preference",
      options: {
        filter: false,
        sort: false,
        filterType: "multiselect",
        customHeadRender: () => (
          <TableCell
            style={{ fontSize: "17px", fontWeight: "600" }}
            align="center"
          >
            Timeprefrence
          </TableCell>
        ),
      },
    },
    {
      name: "action",
      label: "Actions",
      options: {
        filter: false,
        customBodyRender: (value) => (
          <ActionButtons
            value={value}
            hasUpdateTicketPermission={hasUpdateTicketPermission}
            hasCloseTicketPermission={hasCloseTicketPermission}
            hasBroadcastPermission={hasBroadcastPermission}
            hasRemarkTicketPermission={hasRemarkTicketPermission}
            hasPutOnHoldPermission={hasPutOnHoldPermission}
            hasAssignUnassignPermission={hasAssignUnassignPermission}
            handleViewTicket={handleViewTicket}
            handleEditTicket={handleEditTicket}
            handlePutOnHold={handleOpenPutOnHoldPopup}
            handleRemoveHold={handleRemoveHold}
            isPuttingOnHold={isPuttingOnHold}
            isRemovingHold={isRemovingHold}
            clickCloseTicket={clickCloseTicket}
            handleBroadcastButtonClick={handleBroadcastButtonClick}
            handleRebookOpen={handleRebookOpen}
            handleRemarks={handleRemarks}
            reopen={reopen}
            handleOpenTechnicianPopup={handleOpenTechnicianPopup}
          />
        ),
      },
    },
  ];

  // Initialize data with saved filters
  useEffect(() => {
    if (isInitialized) return;

    fetchData(appliedFilters);
    setIsInitialized(true);
  }, [isInitialized, fetchData, appliedFilters]);

  if (!hasViewTicketPermission) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
          fontSize: "18px",
          color: "red",
          fontWeight: "bold",
        }}
      >
        No permission to view tickets. Please contact administrator.
      </div>
    );
  }

  return (
    <div>
      <TicketFilterB2B
        onFilterSubmit={handleFilterSubmit}
        loading={isFilterLoading}
        initialFilters={appliedFilters}
      />

      <RebookModal
        isOpen={openRebook}
        handleRebookClose={handleRebookClose}
        Ticket_id={rebookTicketId}
      />

      {/* CloseTicketRemarksModal */}
      {/* <CloseTicketRemarksModal
        open={isCloseTicketRemarksModalOpen}
        handleClose={handleCloseCloseTicketRemarksModal}
        handleCloseTicket={handleCloseTicket}
        getSingleTicketData={getSingleTicketData?.results}
      /> */}

      <JobCompletedModal
        open={isCloseTicketRemarksModalOpen}
        onClose={handleCloseCloseTicketRemarksModal}
        ticketId={closingTicketId}
      />

      {isEditOpenAddressPincode && (
        <TicketEditPincodePopup
          open={isEditOpenAddressPincode}
          isPincodeUpdating={isPincodeUpdating}
          handleUpdatePincode={handleUpdatePincode}
          handleClose={handleCloseEditPincodePopup}
          isEditOpenTicketDetails={isEditOpenTicketDetails}
        />
      )}

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button
          variant="contained"
          style={{
            backgroundColor: "green",
            color: "white",
            padding: "8px 10px",
            borderRadius: "0",
            marginBottom: "10px",
            height: "38px",
            width: "70px",
          }}
        >
          <div>PAID</div>
          <div>{totalClosingPrice}</div>
        </Button>
      </Box>

      <MUIDataTable
        title={"Requests"}
        data={ticketsList}
        columns={columns}
        options={getTableOptions([], () => {}, hasDownloadTicketPermission)}
      />

      {isAssignTechnicianPopupOpen && (
        <AssignTechnicianPopup
          open={isAssignTechnicianPopupOpen}
          handleClose={handleCloseAssignTechnicianPopup}
          technicianList={technicianList}
          isForChange={isForChange}
          ticketDetailsForAssignTechnician={ticketDetailsForAssignTechnician}
          isTechnicianAssigning={isTechnicianAssigning}
          handleAssignTechnician={handleAssignTechnician}
          handleChangeTechnician={handleChangeTechnician}
          ticketObjId={ticketDetailsForAssignTechnician?._id}
        />
      )}

      {doesFileUploadErrorExists && (
        <FileUploadErrors
          doesFileUploadErrorExists={doesFileUploadErrorExists}
          errorsList={errorsList}
          handleCloseFileUploadErrors={() =>
            setDoesFileUploadErrorExists(false)
          }
        />
      )}

      {hasRemarkTicketPermission && (
        <RemarksPopup
          open={isRemarksPopupOpen}
          handleClose={handleCloseRemarksPopup}
          onSaveRemarks={handleCloseRemarksPopup}
          selectedTicketId={selectedTicketId}
          username={username}
          initialRemarks={initialRemarks}
          processedname={processedname}
        />
      )}

      {hasPutOnHoldPermission && (
        <PutOnHoldPopup
          open={isPutOnHoldPopupOpen}
          handleClose={handleClosePutOnHoldPopup}
          ticketId={selectedTicketForHold?._id}
          onSave={handlePutOnHold}
        />
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AllClientTickets;
